import { Axios } from "api/axios";
import MultiSelect from "Common/MultiSelect";
import { useFormik } from "formik";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addCategory,
  fetchCategoryById,
  updateCategory,
} from "slices/category/thunk";
import * as Yup from "yup";

interface Option {
  id: string;
  label: string;
  value?: string;
}

const CategoryForm: React.FC = () => {
  const [options, setOptions] = useState<Option[]>([]);
  const [optionSelected, setSelected] = useState<Option[]>([]);
  const [attributesError, setAttributesError] = useState<string>("");
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = useHandleBack("/category");
  const isEdit = Boolean(id);
  const [initialData, setInitialData] = useState({
    categoryName: "",
    web: "6",
    system: "3",
  });
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const passedData: any = location.state?.categoryData || null;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showValueModal, setShowValueModal] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // console.log(options);

  // attribute dropdown
  const fetchAttribute = async () => {
    try {
      const list = await Axios("api/attributes-master/attributes-dropdown/");
      if (list.data?.data) {
        const opData: Option[] = list.data?.data?.map((el: any) => {
          return {
            id: el?.attribute_id,
            label: el?.attribute_name,
            value: el?.attribute_id,
          };
        });
        setOptions(opData);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAttribute();
  }, []);

  const handleChange = (selected: Option[]) => {
    // console.log("=== MultiSelect onChange triggered ===");
    // console.log("Selected attributes:", selected);
    // console.log("Selected count:", selected?.length);

    // Directly set the selected values without additional filtering
    // The MultiSelect component already handles cleaning the data
    setSelected(selected || []);

    // Clear error when attributes are selected
    if (selected && selected.length > 0 && attributesError) {
      setAttributesError("");
    }
  };

  // Edit mode
  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchCategoryById(id))
        .then((res: any) => {
          setInitialData((prev: any) => ({
            ...prev,
            categoryName: res?.payload?.category_name,
            web: res?.payload?.web_per,
            system: res?.payload?.system_per,
          }));

          if (res?.payload?.attributes && res?.payload?.attributes.length > 0) {
            const existingAttributes = res.payload.attributes.map(
              (attr: any) => ({
                id: attr.attribute_id || attr.id,
                label: attr.attribute_name || attr.label,
                value: attr.attribute_id || attr.id,
              })
            );
            setSelected(existingAttributes);
          }
        })
        .catch((err: any) => {
          toast.error("Failed to load category details");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEdit]);

  const validateAttributes = (): boolean => {
    if (!optionSelected || optionSelected.length === 0) {
      setAttributesError("Please select at least one attribute");
      return false;
    }
    setAttributesError("");
    return true;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData,
    // validate: (values) => {
    //   const errors: { [key: string]: string } = {};
    //   if (!optionSelected || optionSelected.length === 0) {
    //     errors.attributes = "Please select at least one attribute";
    //     setAttributesError("Please select at least one attribute");
    //   } else {
    //     setAttributesError("");
    //   }
    //   return errors;
    // },
    validationSchema: Yup.object({
      categoryName: Yup.string()
        .required("Please enter category name")
        .matches(
          /^[A-Za-z\s-]+$/,
          "Category name must contain only letters, spaces, and hyphens"
        ),
      ...(isEdit
        ? {}
        : {
            web: Yup.number().required("Please enter %web"),
            system: Yup.number().required("Please enter %system"),
          }),
    }),
    onSubmit: async (values) => {
      // if (!validateAttributes()) {
      //   toast.error("Please select at least one attribute");
      //   return;
      // }

      const transformedAttributes = optionSelected.map((attr) => ({
        id: attr.id,
        label: attr.label,
      }));

      let payload = {
        category_name: values.categoryName,
        attributes: transformedAttributes,
        web_per: values.web,
        system_per: values.system,
      };

      try {
        if (isEdit) {
          await dispatch(updateCategory({ id, ...payload })).unwrap();
          toast.success("Category updated successfully");
        } else {
          const newPayload = {
            ...payload,
            web_per: values.web,
            system_per: values.system,
          };
          await dispatch(addCategory(newPayload)).unwrap();
          toast.success("Category added successfully");
        }
        // setTimeout(() => {
        //   // navigate("/category");
        //   handleBack();
        // }, 12000);
        handleBack();
      } catch (error: any) {
        console.error("Add category error:", error);
        toast.error(error?.message || "Failed to add category");
      }
    },
  });

  // function to handle new Attrinute addition
  const handleAddNewValue = async (value: string) => {
    try {
      setIsSubmitting(true);
      const response = await Axios.post("/api/attributes-master/create/", {
        attribute_name: value.trim(),
      });

      console.log(response);

      if (response.data.data) {
        const newOption = {
          id: response.data.data.attribute_id,
          label: response.data.data.attribute_name,
          value: response.data.data.attribute_id, // Not necessory but that's the structure
        };

        setOptions((prev: any) => [...prev, newOption]);

        setSelected((prev: any) => [...prev, newOption]);

        toast.success("New Attribute added successfully");
        setShowValueModal(false);
        setNewValue("");
      }
    } catch (error: any) {
      console.error("Error adding new Attribute:", error);
      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-content px-4">
      {isEdit && isLoading ? (
        <div
          className="text-center py-5"
          style={{
            background: "#fff",
            borderRadius: "10px",
            padding: "30px",
            boxShadow: "0 0 15px rgba(0,0,0,0.05)",
          }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div
          style={{
            background: "#fff",
            borderRadius: "10px",
            padding: "30px",
            boxShadow: "0 0 15px rgba(0,0,0,0.05)",
          }}
        >
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{ fontSize: "24px", cursor: "pointer" }}
                    onClick={handleBack}
                  >
                    ←
                  </span>
                  <h5 className="mb-0 fw-bold">
                    {isEdit ? "Edit Category" : "Add Category"}
                  </h5>
                </div>

                <div className="text-muted">
                  <span
                    className="text-muted"
                    style={{ fontSize: "0.9rem", cursor: "pointer" }}
                    onClick={handleBack}
                  >
                    Category
                  </span>{" "}
                  &gt;{" "}
                  <span
                    className="text-muted"
                    style={{ fontSize: "0.9rem", cursor: "pointer" }}
                  >
                    {isEdit ? "Edit Category" : "Add Category"}
                  </span>{" "}
                </div>
              </div>

              <Col lg={6}>
                <div className="mb-3">
                  <Form.Label>
                    Category Name<span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="categoryName"
                    placeholder="Enter Category Name"
                    value={formik.values.categoryName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.categoryName &&
                      formik.touched.categoryName
                    }
                    style={{
                      height: "40px",
                      lineHeight: "1.5",
                      fontSize: "0.875rem",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.categoryName}
                  </Form.Control.Feedback>
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  {/* <Form.Label>
                    Attributes <span className="text-danger">*</span>
                  </Form.Label> */}

                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                    <Form.Label className="fw-semibold d-flex align-items-center">
                      Attributes
                      {/* <span className="text-danger">*</span> */}
                    </Form.Label>

                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="py-0 mt-1 mt-lg-0"
                      onClick={() => {
                        //   setSelectedAttribute(obj);
                        setShowValueModal(true);
                      }}
                    >
                      +
                    </Button>
                  </div>
                  <div>
                    <MultiSelect
                      options={options}
                      onChange={handleChange}
                      value={optionSelected}
                      isSelectAll={true}
                      menuPlacement="bottom"
                      className={`w-100`}
                      styles={{
                        control: (base: any, state: any) => ({
                          ...base,
                          borderColor: attributesError
                            ? "#dc3545"
                            : base.borderColor,
                          "&:hover": {
                            borderColor: attributesError
                              ? "#dc3545"
                              : base.borderColor,
                          },
                        }),
                      }}
                      placeholder="Select attributes..."
                    />
                  </div>
                  {attributesError && (
                    <div className="invalid-feedback d-block">
                      {attributesError}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {!isEdit && (
              <Row>
                <Col lg={6}>
                  <div className="mb-3">
                    <Form.Label>
                      %WEB<span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="web"
                      placeholder="Enter %WEB"
                      value={formik.values.web}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={!!formik.errors.web && formik.touched.web}
                      style={{
                        height: "40px",
                        lineHeight: "1.5",
                        fontSize: "0.875rem",
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.web}
                    </Form.Control.Feedback>
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Form.Label>
                      %System<span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="system"
                      placeholder="Enter %system"
                      value={formik.values.system}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.system && formik.touched.system
                      }
                      style={{
                        height: "40px",
                        lineHeight: "1.5",
                        fontSize: "0.875rem",
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.system}
                    </Form.Control.Feedback>
                  </div>
                </Col>
              </Row>
            )}

            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="outline-secondary"
                style={{
                  color: "#878899",
                  borderColor: "#B0BAC9",
                  backgroundColor: "transparent",
                }}
                onClick={handleBack}
              >
                Cancel
              </Button>

              <Button type="submit" variant="primary">
                {isEdit ? "Update" : "Add"}
              </Button>
            </div>
          </Form>

          {/* Add New Attribute Modal */}
          <Modal
            show={showValueModal}
            onHide={() => {
              setShowValueModal(false);
              setNewValue("");
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Add New Attribute</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group>
                <Form.Label>Attribute Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter new attribute"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowValueModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!newValue.trim() || isSubmitting}
                onClick={() => {
                  handleAddNewValue(newValue);
                }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  "Add"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default CategoryForm;
